// src/features/system/developer/components/video-gallery/groupByDay.ts
// ────────────────────────────────────────────────────────────────────────────
// Web parity of PACMobile's `VideoListRow.toGroupedRows()`.
//
// Same contract:
//   - Day groups newest-first.
//   - Within each day, items oldest→newest (chronological).
//   - GrandTotal row appended last.
//
// Pure TS, no React deps — keeps the panel a thin renderer.
// ────────────────────────────────────────────────────────────────────────────
import type { VideoFeedItem } from './types';
import { referenceCreatedAtMs } from './format';

export type VideoGroupedRow =
  | {
      kind: 'header';
      key: string;
      dayStartMs: number;
      count: number;
      totalBytes: number;
      totalDurationMs: number;
    }
  | {
      kind: 'video';
      key: string;
      item: VideoFeedItem;
    }
  | {
      kind: 'grand-total';
      key: 'grand-total';
      count: number;
      totalBytes: number;
      daysCovered: number;
      totalDurationMs: number;
    };

function startOfDayMs(epochMs: number): number {
  const d = new Date(epochMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Group video items by local-calendar day.
 *
 * Input is expected newest-first. We sort defensively by the gallery's
 * canonical creation timestamp (`referenceCreatedAtMs`) so grouping stays
 * stable even when backend UTC timestamps cross local midnight.
 */
export function groupByDay(items: ReadonlyArray<VideoFeedItem>): VideoGroupedRow[] {
  if (!items || items.length === 0) return [];

  // Defensive: ensure DESC by created_at_ms (fallback to 0 keeps order stable).
  const sorted = [...items].sort(
    (a, b) => referenceCreatedAtMs(b) - referenceCreatedAtMs(a),
  );

  const rows: VideoGroupedRow[] = [];
  let currentDayStart = Number.NEGATIVE_INFINITY;
  let dayBuffer: VideoFeedItem[] = [];
  let dayCount = 0;
  let dayBytes = 0;
  let dayDurMs = 0;

  let grandCount = 0;
  let grandBytes = 0;
  let grandDurMs = 0;
  const daySet = new Set<number>();

  const flush = () => {
    if (currentDayStart === Number.NEGATIVE_INFINITY) return;
    rows.push({
      kind:            'header',
      key:             `header-${currentDayStart}`,
      dayStartMs:      currentDayStart,
      count:           dayCount,
      totalBytes:      dayBytes,
      totalDurationMs: dayDurMs,
    });
    // Buffer was filled DESC → reverse so the user reads each day ASC.
    for (let i = dayBuffer.length - 1; i >= 0; i--) {
      const it = dayBuffer[i];
      rows.push({ kind: 'video', key: `video-${it.video_id}`, item: it });
    }
    dayBuffer = [];
  };

  for (const it of sorted) {
    const ms = referenceCreatedAtMs(it);
    const dayStart = startOfDayMs(ms);
    if (dayStart !== currentDayStart) {
      flush();
      currentDayStart = dayStart;
      dayCount = 0;
      dayBytes = 0;
      dayDurMs = 0;
    }
    dayBuffer.push(it);
    dayCount += 1;
    dayBytes += it.byte_length || 0;
    dayDurMs += it.duration_ms || 0;
    grandCount += 1;
    grandBytes += it.byte_length || 0;
    grandDurMs += it.duration_ms || 0;
    daySet.add(dayStart);
  }
  flush();

  rows.push({
    kind:            'grand-total',
    key:             'grand-total',
    count:           grandCount,
    totalBytes:      grandBytes,
    daysCovered:     daySet.size,
    totalDurationMs: grandDurMs,
  });

  return rows;
}

/** User-facing day header label: "Today", "Yesterday", or "Mon, May 13". */
export function formatDayHeader(dayStartMs: number, now = Date.now()): string {
  const today = startOfDayMs(now);
  const oneDay = 86_400_000;
  if (dayStartMs === today)             return 'Today';
  if (dayStartMs === today - oneDay)    return 'Yesterday';
  return new Date(dayStartMs).toLocaleDateString(undefined, {
    weekday: 'short',
    month:   'short',
    day:     'numeric',
    year:    new Date(dayStartMs).getFullYear() === new Date(now).getFullYear() ? undefined : 'numeric',
  });
}
